import React, { createContext, useContext, useState, useRef, useEffect } from 'react';

const AnalyticsContext = createContext();

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider = ({ children }) => {
  const [contentData, setContentData] = useState(null);
  const [visitorsData, setVisitorsData] = useState(null);
  const [followersData, setFollowersData] = useState(null);
  
  const [loadingContent, setLoadingContent] = useState(false);
  const [loadingVisitors, setLoadingVisitors] = useState(false);
  const [loadingFollowers, setLoadingFollowers] = useState(false);

  const hasFetched = useRef(false);
  const isFetching = useRef(false);

  const N8N_BASE_URL = 'https://n8n.acespireconsulting.com/webhook';

  // ✅ GET linkedinAccountId FROM localStorage
  const getLinkedInAccountId = () => {
    const accountId = localStorage.getItem('selectedLinkedInAccountId');
    if (!accountId) {
      console.error('❌ No LinkedIn account selected');
      return null;
    }
    return parseInt(accountId);
  };

  // ✅ LISTEN FOR ACCOUNT CHANGES
  useEffect(() => {
    const handleAccountChange = () => {
      console.log('🔄 Account changed - clearing analytics cache');
      hasFetched.current = false;
      setContentData(null);
      setVisitorsData(null);
      setFollowersData(null);
      
      // Re-fetch with new account
      const accountId = getLinkedInAccountId();
      if (accountId) {
        const defaultDateRange = {
          start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          end: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        };
        fetchAllAnalytics(defaultDateRange, {}, 'Location', true);
      }
    };

    window.addEventListener('accountChanged', handleAccountChange);
    return () => window.removeEventListener('accountChanged', handleAccountChange);
  }, []);

  const fetchAllAnalytics = async (dateRange, visitorParams = {}, followerDemographic = 'Location', force = false) => {
    // ✅ Check linkedinAccountId first
    const linkedinAccountId = getLinkedInAccountId();
    if (!linkedinAccountId) {
      console.error('❌ Cannot fetch analytics: No LinkedIn account selected');
      return;
    }

    if (hasFetched.current && !force && contentData && visitorsData && followersData) {
      console.log('⏭️ Analytics data already cached, skipping fetch');
      return;
    }

    if (isFetching.current) {
      console.log('⚠️ Analytics fetch already in progress, skipping...');
      return;
    }

    isFetching.current = true;
    console.log(`🔄 Fetching ALL analytics for account ${linkedinAccountId}...`);

    const contentPromise = fetchContentAnalytics(linkedinAccountId, dateRange);
    const visitorsPromise = fetchVisitorAnalytics(linkedinAccountId, dateRange, visitorParams);
    const followersPromise = fetchFollowerAnalytics(linkedinAccountId, dateRange, followerDemographic);

    try {
      await Promise.all([contentPromise, visitorsPromise, followersPromise]);
      console.log('✅ All analytics fetched successfully');
      hasFetched.current = true;
    } catch (error) {
      console.error('❌ Error fetching analytics:', error);
      hasFetched.current = false;
    } finally {
      isFetching.current = false;
    }
  };

  const fetchContentAnalytics = async (linkedinAccountId, dateRange) => {
    setLoadingContent(true);
    try {
      console.log('📊 Fetching content analytics...');
      const response = await fetch(
        `${N8N_BASE_URL}/content-analytics?linkedinAccountId=${linkedinAccountId}&startDate=${dateRange.start}&endDate=${dateRange.end}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // ✅ CHECK: If session expired
    if (data.requiresReconnection || data.error === 'LinkedIn session expired') {
      console.log('🔴 LinkedIn session expired during content analytics');
      // Dispatch custom event for session expiry
      window.dispatchEvent(new CustomEvent('linkedinSessionExpired'));
      return;
    }
      const contentInfo = Array.isArray(data) ? data[0] : data;
      console.log('✅ Content analytics complete');
      setContentData(contentInfo);
    } catch (error) {
      console.error('❌ Content analytics error:', error);
    } finally {
      setLoadingContent(false);
    }
  };

  const fetchVisitorAnalytics = async (linkedinAccountId, dateRange, params) => {
    setLoadingVisitors(true);
    try {
      console.log('👀 Fetching visitor analytics...');
      const { metric = 'page views', page = 'all pages', type = 'all visitors' } = params;
      const response = await fetch(
        `${N8N_BASE_URL}/visitor-analytics?linkedinAccountId=${linkedinAccountId}&startDate=${dateRange.start}&endDate=${dateRange.end}&metric=${metric}&page=${page}&type=${type}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // ✅ CHECK: If session expired
    if (data.requiresReconnection || data.error === 'LinkedIn session expired') {
      console.log('🔴 LinkedIn session expired during content analytics');
      // Dispatch custom event for session expiry
      window.dispatchEvent(new CustomEvent('linkedinSessionExpired'));
      return;
    }
      const visitorInfo = Array.isArray(data) ? data[0] : data;
      console.log('✅ Visitor analytics complete');
      setVisitorsData(visitorInfo);
    } catch (error) {
      console.error('❌ Visitor analytics error:', error);
    } finally {
      setLoadingVisitors(false);
    }
  };

  const fetchFollowerAnalytics = async (linkedinAccountId, dateRange, demographic) => {
    setLoadingFollowers(true);
    try {
      console.log('👥 Fetching follower analytics...');
      const response = await fetch(
        `${N8N_BASE_URL}/follower-analytics?linkedinAccountId=${linkedinAccountId}&startDate=${dateRange.start}&endDate=${dateRange.end}&demographic=${demographic}`
      );
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // ✅ CHECK: If session expired
    if (data.requiresReconnection || data.error === 'LinkedIn session expired') {
      console.log('🔴 LinkedIn session expired during content analytics');
      // Dispatch custom event for session expiry
      window.dispatchEvent(new CustomEvent('linkedinSessionExpired'));
      return;
    }
      const followerInfo = Array.isArray(data) ? data[0] : data;
      console.log('✅ Follower analytics complete');
      setFollowersData(followerInfo);
    } catch (error) {
      console.error('❌ Follower analytics error:', error);
    } finally {
      setLoadingFollowers(false);
    }
  };

  const refreshAllAnalytics = (dateRange, visitorParams, followerDemographic) => {
    console.log('🔄 Manual refresh triggered - clearing cache');
    hasFetched.current = false;
    setContentData(null);
    setVisitorsData(null);
    setFollowersData(null);
    fetchAllAnalytics(dateRange, visitorParams, followerDemographic, true);
  };

  const isLoading = loadingContent || loadingVisitors || loadingFollowers;

  const value = {
    contentData,
    visitorsData,
    followersData,
    loadingContent,
    loadingVisitors,
    loadingFollowers,
    isLoading,
    fetchAllAnalytics,
    refreshAllAnalytics,
    hasFetched: hasFetched.current
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};